import React, { useEffect, useState, useMemo, useContext, useRef } from "react";
import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import {
  AuthenticationService,
  ConfigurationService,
  CoverageService,
  AppSettingsService,
  CommonService,
  PendoService
} from "@services";
import PatientPANotificationPopup from "./PatientPANotificationPopup";
import Statement from "../CommonComponent/Statement";
import DynamicHtml from "../CommonComponent/DynamicHtml";
import SearchIcon from "@mui/icons-material/Search";
import PatientCardDetails from "./PatientCardDetails";
import Footer from "../CommonComponent/Footer";
import PatientSearchCoverage from "./PatientSearchCoverage";
import DebugMessage from "../CommonComponent/DebugMessage";
import LoaderWithIcon from "../CommonComponent/LoaderWithIcon";
import { ConfigurationContext } from "../../../contexts/Configuration";
import { SearchContext } from "../../../contexts/Search";

const PatientCoverageFinder = ({ token, indicationid }) => {
  const NO_RESULTS_FOUND_MESSAGE = "No results found";
  const NO_PLAN_DETAILS_MESSAGE = "No plan details";
  const defaultChannels = [
    "Show All",
    "Commercial",
    "Health Exchange",
    "Medicare",
    "Managed Medicaid",
    "State Medicaid",
  ];
  const { appliedConfiguration, setAppliedConfiguration } = useContext(ConfigurationContext);
  const { setSearchTermDisplay, benefitType, setBenefitType, productId, productName, indicationName, setProductId, setProductName, setIndicationName } = useContext(SearchContext);
  const [channels, setChannels] = useState(defaultChannels);
  const [clientProductConfigurations, setClientProductConfigurations] = useState(null);
  const [selectedBenefitCoverageCardSettings, setSelectedBenefitCoverageCardSettings] = useState([]);
  const [customFooter, setCustomFooter] = useState("");
  const [messageForNoPlanCard, setMessageForNoPlanCard] = useState("");
  const [isLoadingPage, setIsLoadingPage] = useState(true);
  const [stylePath, setStylePath] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [plans, setPlans] = useState([]);
  const [allCoverages, setAllCoverages] = useState([]);
  const [selectedPlanCardDetails, setSelectedPlanCardDetails] = useState("");
  const [selectedPlanValue, setSelectedPlanValue] = useState("");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [stateId, setStateId] = useState("");
  const [liveTypeId, setLiveTypeId] = useState("");
  const [statusDataField, setStatusDataField] = useState("");
  const [stDataField, setSTDataField] = useState("");
  const [paDataField, setPADataField] = useState("");
  const [header, setHeader] = useState("");
  const [dynamicStatement, setDynamicStatement] = useState("");
  const [subHeader, setSubHeader] = useState("");
  const [benefitHierarchyName, setBenefitHierarchyName] = useState("");
  const [selectedZipCode, setSelectedZipCode] = useState("");
  const [geographicSearchTerm, setGeographicSearchTerm] = useState("");
  const [selectedInsuranceType, setInsuranceType] = useState(null);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [paButtonTitle, setPAButtonTitle] = useState("");
  const [showPlanCard, setShowPlanCard] = useState(false);
  const [isPASTDetailPresent, setIsPASTDetailPresent] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [showPlanSearch, setShowPlanSearch] = useState(true);
  const [isInsuranceChanged, setIsInsuranceChanged] = useState(false);
  const [isGeographicSearchChanged, setIsGeographicSearchChanged] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const [showComponents, setShowComponents] = useState(false);
  const [isBenefitTypeUpdated, setIsBenefitTypeUpdated] = useState(false);
  const pastPositiveValidValues = ["yes", "true", "1"];
  const [isDisableSearch, setDisableSearch] = useState(true);
  const dynamicHtmlRef = useRef(null);
  useEffect(() => {
    if (isBenefitTypeUpdated) {
      if (!isDisableSearch) { searchCoverageClick(); }
      setIsBenefitTypeUpdated(false);
    }
  }, [isBenefitTypeUpdated]);

  // Handler for Search button click
  const searchCoverageClick = async () => {
    setSearchTermDisplay(geographicSearchTerm);
    setIsGeographicSearchChanged(false);
    setIsInsuranceChanged(false);
    setIsLoadingPage(true);
    setShowPlanSearch(false);
    setMessageForNoPlanCard("");
    setSelectedPlanValue("");
    setPlans([]);
    setAllCoverages([]);
    setSelectedPlanCardDetails("");
    setShowPlanCard(false);
    setShowNotification(false);
    setShowComponents(false);
    const config = clientProductConfigurations.configurations.find(
      (c) =>
        (c.coverageFinderConfiguration?.benefitType === benefitType ||
          c.coverageFinderConfiguration?.benefitType.toLowerCase().includes("both")) &&
        (!indicationid || c.indicationId.toString() === indicationid)
    );
    if (!config) {
      console.error("Config not found for " + benefitType + " benefit type and indication Id: " + indicationid);
      setIsLoadingPage(false);
      return;
    }
    const coverageRequest = {
      ProjectId: config.projectId,
      ZipCode: selectedZipCode,
      StateId: stateId,
      NPI: null,
      LiveTypeId: liveTypeId,
      ProductId: selectedProduct.ProductId,
      IndicationId: config.indicationId,
      resultName:
        benefitType.toLowerCase() === "pharmacy"
          ? config.coverageFinderConfiguration.pharmacyResultName
          : config.coverageFinderConfiguration.resultName,
    };
    const coverageResponse = await CoverageService.getInstance().getCoverage(coverageRequest);
    setStateId(coverageResponse?.stateId);
    if (coverageResponse != null && coverageResponse.coverages != null) {
      setAllCoverages(coverageResponse.coverages);
      let filteredData = coverageResponse.coverages.filter(
        (p) => p.Channel.toLowerCase().indexOf(selectedInsuranceType.toLowerCase()) > -1
      );
      if (filteredData != null && filteredData.length > 0) {
        setDynamicStatement(coverageResponse.statement);
        setPlans(filteredData);
        CommonService.hideCfHeaderStatement(true, dynamicHtmlRef);
        setShowComponents(true);
      } else {
        setPlans([]);
        setMessageForNoPlanCard(NO_RESULTS_FOUND_MESSAGE);
        setShowComponents(true);
      }
    } else {
      setPlans([]);
      setMessageForNoPlanCard(NO_RESULTS_FOUND_MESSAGE);
      setShowComponents(true);
    }
    setShowPlanSearch(true);
    setIsLoadingPage(false);
  };

  useEffect(() => {
    let ignore = false;
    let tokenProducts = [];
    if (token) {
      const jwtPayload = CommonService.parseJwt(token);
      tokenProducts = JSON.parse(jwtPayload.products);
      setProductName(tokenProducts[0].Name);
      setProductId(tokenProducts[0].ProductId);
      setSelectedProduct(tokenProducts[0]);
      const jwtWebsiteType = jwtPayload.websiteType ?? 2;
      const cdnUrl = AppSettingsService.getInstance().getCDNUrl();
      setStylePath(`${cdnUrl}css/${jwtPayload.clientId}_${tokenProducts[0].ProductId}_${jwtWebsiteType}.css`);
    } else {
      console.log("Token is null or empty");
    }

    async function getConfig() {
      const requestBody = {
        ProductId: tokenProducts[0].ProductId,
      };

      const configResponse = await ConfigurationService.getInstance().getConfiguration(requestBody);
      if (!ignore) {
        applyConfiguration(configResponse);
        setIsLoadingPage(false);
      }
    }

    if (token) {
      AuthenticationService.getInstance().setToken(token);
      getConfig();
    }

    return () => {
      ignore = true;
    };
  }, [token]);

  useMemo(() => {
    changeConfiguration(indicationid);
  }, [indicationid]);

  function changeConfiguration(indicationId) {
    if (clientProductConfigurations?.configurations) {
      if (indicationId) {
        let jwtWebsiteType = Number(CommonService.parseJwt(token).websiteType ?? 2);
        const config = clientProductConfigurations.configurations.find(
          (x) =>
            Number(x.indicationId) === Number(indicationid) &&
            x.coverageFinderConfiguration != null &&
            Number(x.coverageFinderConfiguration.websiteType) === jwtWebsiteType
        );
        if (config) {
          setAppliedConfiguration(config);
          setAllConfigValues(config);
        } else {
          console.log("no configuration found for indication");
        }
      } else {
        console.log("indicationId is null or empty");
      }
    }
  }

  function setAllConfigValues(config) {
    setHeader(config.coverageFinderConfiguration?.header);
    const benefitTypeId = config.coverageFinderConfiguration?.benefitType?.toLowerCase().includes("pharmacy") ? 2 : 1;
    setBenefitTypeConfigValues(config, benefitTypeId);
    const cfSettings = config.settings;
    setChannels(config.projectChannels?.map((c) => c.channelName));
    if (config.indicationStylesheetURL) {
      setStylePath(config.indicationStylesheetURL);
    }

    if (cfSettings?.findIndex((setting) => setting.settingId === 81 && setting.isEnabled === true) > -1) {
      setDebugMode(true);
    }
    const indicationName = config.coverageFinderConfiguration?.indicationName ?? config.indicationName;
    setIndicationName(indicationName);
  }

  function setBenefitTypeConfigValues(config, benefitTypeId) {
    setLiveTypeId(benefitTypeId == 2 ? 4 : 1);
    setBenefitHierarchyName(benefitTypeId == 2 ? config.coverageFinderConfiguration?.pharmacyResultName : config.coverageFinderConfiguration?.resultName);
    setSubHeader(benefitTypeId == 2 ? config.coverageFinderConfiguration?.pharmacySubHeader : config.coverageFinderConfiguration?.subHeader);
    setCustomFooter(benefitTypeId == 2 ? config.coverageFinderConfiguration?.pharmacyFooterText : config.coverageFinderConfiguration?.footerText);
    const paNotificationConfig = config.coverageFinderProjectAdditionalDetails?.find(
      (x) => Number(x.benefitTypeId) === Number(benefitTypeId)
    );
    if (paNotificationConfig) {
      setNotificationMessage(paNotificationConfig?.boxTitle);
      setPAButtonTitle(paNotificationConfig?.buttonTitle);
    }
    const patientCoverageDataFields = config.patientCoverageDataFields?.find(
      (x) => (x.isPharmacy == (benefitTypeId == 2))
    );
    if (patientCoverageDataFields) {
      setPADataField(patientCoverageDataFields?.paDataFieldName);
      setSTDataField(patientCoverageDataFields?.stDataFieldName);
      setStatusDataField(patientCoverageDataFields?.statusDataFieldName);
    }
    setSelectedBenefitCoverageCardSettings(config.coverageCardSettings?.filter(
      (x) => (x.isPharmacy == (benefitTypeId == 2))
    ));
  }

  const applyConfiguration = (configResponse) => {
    if (configResponse) {
      setClientProductConfigurations(configResponse);

      let jwtWebsiteType = Number(CommonService.parseJwt(token).websiteType ?? 2);
      let config = jwtWebsiteType
        ? configResponse.configurations.find(
          (x) =>
            x.coverageFinderConfiguration != null &&
            Number(x.coverageFinderConfiguration.websiteType) === jwtWebsiteType
        )
        : configResponse.configurations[0];

      if (indicationid) {
        config = configResponse.configurations.find(
          (x) =>
            Number(x.indicationId) === Number(indicationid) &&
            x.coverageFinderConfiguration != null &&
            Number(x.coverageFinderConfiguration.websiteType) === jwtWebsiteType
        );
      }
      setAppliedConfiguration(config);
      setBenefitType(config.coverageFinderConfiguration.benefitType.toLowerCase().includes("pharmacy") ? "Pharmacy" : "Medical");
      setAllConfigValues(config);
    } else {
      console.error("Config response is null or empty");
    }
  };

  const handleZipSearch = (value) => {
    setShowComponents(false);
    CommonService.hideCfHeaderStatement(false, dynamicHtmlRef);
    onGeographicalValueChange();
    setIsGeographicSearchChanged(true);
    if (!value || value === "")
      handleEmptySearch();
    else {
      setSearchTermDisplay("");
      setSelectedZipCode(value);
      setGeographicSearchTerm(value);
      setStateId("");
    }
  };

  const handleStateSearch = (value) => {
    setShowComponents(false);
    CommonService.hideCfHeaderStatement(false, dynamicHtmlRef);
    onGeographicalValueChange();
    setIsGeographicSearchChanged(true);
    if (!value || value === "")
      handleEmptySearch();
    else {
      setSearchTermDisplay("");
      setGeographicSearchTerm(value);
      const searchState = appliedConfiguration?.states?.find((s) => s.name.toLowerCase() === value?.toLowerCase());
      setStateId(searchState?.stateId);
      setSelectedZipCode(searchState?.stateId ? "" : value);
    }
  };

  function handleEmptyPlanSearch() {
    setShowPlanCard(false);
    setShowNotification(false);
    setSelectedPlanValue("");
    setShowPlanSearch(true);
    setPlans([]);
    setAllCoverages([]);
    setSelectedPlanCardDetails("");
    setMessageForNoPlanCard("");
  }

  function handleEmptySearch() {
    setSearchTermDisplay("");
    setSelectedZipCode("");
    setStateId("");
    handleEmptyPlanSearch();
    setMessageForNoPlanCard("");
  }

  function onGeographicalValueChange() {
    setStateId("");
    setShowPlanCard(false);
    setShowNotification(false);
    setSelectedPlanValue("");
    setPlans([]);
    setAllCoverages([]);
    setSelectedPlanCardDetails("");
    setMessageForNoPlanCard("");
  }

  const OnInsuranceChange = (newValue) => {
    if (!newValue || newValue === "") {
      setShowComponents(false);
      CommonService.hideCfHeaderStatement(false, dynamicHtmlRef);
      setShowPlanSearch(true);
      setPlans([]);
      return;
    }
    setIsInsuranceChanged(true);
    setInsuranceType(newValue);
    setSelectedPlanValue("");
    setSelectedPlanCardDetails("");
    if (allCoverages != null && allCoverages.length > 0 && newValue) {
      let filteredData = allCoverages.filter(
        (p) => p.Channel.toLowerCase() == newValue.toLowerCase()
      );
      if (filteredData != null && filteredData.length > 0) {
        CommonService.hideCfHeaderStatement(true, dynamicHtmlRef);
        setPlans(filteredData);
        setMessageForNoPlanCard("");
      } else {
        setPlans([]);
        setMessageForNoPlanCard(NO_RESULTS_FOUND_MESSAGE);
      }
    } else {
      setPlans([]);
    }
    setShowPlanCard(false);
    setShowNotification(false);
  };

  const reset = () => {
    setIsGeographicSearchChanged(false);
    setIsInsuranceChanged(false);
    setMessageForNoPlanCard("");
    setSelectedPlanValue("");
    setSelectedPlanCardDetails("");
    setShowPlanCard(false);
    setShowNotification(false);
    setIsLoadingPage(false);
  };

  const handlePlanInputChange = (newInputValue) => {
    (isGeographicSearchChanged || isInsuranceChanged) ? "" : setSelectedPlanValue(newInputValue);
  };

  const handlePlanSearch = (option) => {
    setIsGeographicSearchChanged(false);
    setIsInsuranceChanged(false);
    if (!option) {
      reset();
      return;
    }
    const planName = fetchOptionLabel(option);
    setSelectedPlanValue(planName);
    setSelectedPlan(option);
    setIsLoadingPage(true);
    PendoService.getInstance().trackEvent("Plan Search", "Plan Name", planName, productId, productName, indicationName);
    const paDataFieldValue = option[paDataField] ? option[paDataField].toLowerCase() : "no";
    const stDataFieldValue = option[stDataField] ? option[stDataField].toLowerCase() : "no";
    if (selectedBenefitCoverageCardSettings != null && selectedBenefitCoverageCardSettings.length > 0) {
      let planDetails = selectedBenefitCoverageCardSettings.filter(
        (p) =>
          p.status.toLowerCase().split(",").includes(option[statusDataField]?.toLowerCase()) &&
          p.projectChannelName.toLowerCase().split(",").includes(option.Channel?.toLowerCase()) &&
          (!paDataField || paDataField.toLowerCase() === "none" || p.valuePA?.toLowerCase()?.split(",").includes(paDataFieldValue)) &&
          (!stDataField || stDataField.toLowerCase() === "none" || p.valueST?.toLowerCase()?.split(",").includes(stDataFieldValue))
      );
      if (planDetails.length > 0) {
        let text = planDetails[0].valueText;
        text = text
          .replace("[benefitType]", benefitType)
          .replace(/\[benefit\]/g, benefitType)
          .replace("[productName]", appliedConfiguration?.brandName)
          .replace(/\[product\]/g, appliedConfiguration?.brandName);
        setSelectedPlanCardDetails(text);
        setShowPlanCard(true);
        setShowNotification(true);
        setIsPASTDetailPresent(checkPlanForPASTDetail(option));
        setIsLoadingPage(false);
      } else {
        reset();
        setMessageForNoPlanCard(NO_PLAN_DETAILS_MESSAGE);
      }
    } else {
      reset();
    }
  };

  const handleChangeBenefitType = (newBenefitType, isDisableSearch) => {
    if (newBenefitType) {
      const isPharmacy = newBenefitType.toLowerCase().includes("pharmacy");
      setBenefitType(newBenefitType);
      setBenefitTypeConfigValues(appliedConfiguration, isPharmacy ? 2 : 1);
      setIsBenefitTypeUpdated(true);
      setDisableSearch(isDisableSearch);
      PendoService.getInstance().trackEvent("Change in BenefitType", "handleBenefitType", newBenefitType, productId, productName, indicationName);
    }
    else {
      handleEmptyPlanSearch();
    }
  };

  const checkPlanForPASTDetail = (selectedPlan) => {
    let isPAOrSTPresent = false;
    if (selectedPlan && appliedConfiguration?.coverageFinderConfiguration?.coverageFinderVersionId == 1) {
      const paFieldData = selectedPlan[paDataField];
      const stFieldData = selectedPlan[stDataField];
      isPAOrSTPresent = ((paFieldData && pastPositiveValidValues.includes(paFieldData.toLowerCase())) || (stFieldData && pastPositiveValidValues.includes(stFieldData.toLowerCase())));
    }
    return isPAOrSTPresent;
  };

  const fetchOptionLabel = (option) => {
    switch (benefitHierarchyName?.toLowerCase()) {
      case "controller/payer level":
        return option?.ControllerName;
      case "medical group level":
        return option?.MedicalGroupName;
      case "formulary level":
        return option?.FormularyName;
      default:
        return option?.PlanName;
    }
  };

  return (
    <>
      <link type="text/css" rel="stylesheet" href={stylePath} />
      {
        <Box className="norstella-cf-coverageFinderBox" sx={{ flexGrow: 1 }} part="cfCoverageFinderBox">
          {isLoadingPage && (
            <div className="norstella-cf-overlay">
              <LoaderWithIcon></LoaderWithIcon>
            </div>
          )}
          {header && header.trim() !== "" && (
            <Grid ref={dynamicHtmlRef} item className="norstella-cf-GridItemHeader">
              <DynamicHtml
                input={header != null ? header : ""}
                className="norstella-cf-header"
              />
            </Grid>
          )}
          <Grid container spacing={2} direction={"column"} alignItems={"left"} className="norstella-cf-GridContainer">
            <PatientSearchCoverage
              channels={channels}
              OnInsuranceChange={OnInsuranceChange}
              handleZipSearch={handleZipSearch}
              handleStateSearch={handleStateSearch}
              onGeographicalValueChange={onGeographicalValueChange}
              searchCoverageClick={searchCoverageClick}
              appliedConfiguration={appliedConfiguration}
              onChangeBenefitType={handleChangeBenefitType}
            />
          </Grid>
          <Grid container className="norstella-cf-PatientGridContainerSubHeaderDualBenefit">
            {showComponents && subHeader && subHeader.trim() !== "" && (
              <Grid item className="norstella-cf-PatientGridItemSubHeader">
                <DynamicHtml
                  input={subHeader != null ? subHeader : ""}
                  className="norstella-cf-headerSubHeader"
                />
              </Grid>
            )}
          </Grid>
          {showComponents && dynamicStatement && dynamicStatement.trim() !== "" && (
            <Grid item className="norstella-cf-GridItemStatement">
              <Statement dynamicStatement={dynamicStatement} />
            </Grid>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {(showComponents && showPlanSearch && (
                <div className="norstella-cf-GridItemSearchNames norstella-cf-topMargin">
                  <Autocomplete
                    id="planSelect"
                    className="norstella-cf-searchText"
                    part="cfSearchText"
                    blurOnSelect
                    handleHomeEndKeys
                    disablePortal
                    options={plans}
                    getOptionLabel={fetchOptionLabel}
                    disabled={!plans || plans.length === 0}
                    onChange={(_, newValue) => handlePlanSearch(newValue)}
                    inputValue={selectedPlanValue}
                    onInputChange={(_, newInputValue) => handlePlanInputChange(newInputValue)}
                    style={{ width: "100%" }}
                    noOptionsText={<span style={{ color: "red", fontWeight: "bold" }}>No options</span>}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search Plan Names"
                        className="norstella-cf-txtSearchNames"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <SearchIcon className="norstella-cf-patientSearchIcon" />
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </div>
              ))}
            </Grid>
          </Grid>
          {showNotification && isPASTDetailPresent &&
            (
              <Grid item className="norstella-cf-patientDiv norstella-cf-GridItemSearchNames norstella-cf-topMargin">
                <PatientPANotificationPopup
                  selectedPlan={selectedPlan}
                  selectedPlanValue={selectedPlanValue}
                  stateId={stateId}
                  notificationMessage={notificationMessage}
                  buttonText={paButtonTitle}
                />
              </Grid>
            )
          }
          {showPlanCard
            ? (
              <Grid item className="norstella-cf-patientDiv norstella-cf-GridItemPatientCardDetails ">
                <PatientCardDetails resultname={selectedPlanValue} channelName={selectedPlan?.Channel || ""} selectedPlanCardDetails={selectedPlanCardDetails} config={clientProductConfigurations} />
              </Grid>
            )
            : messageForNoPlanCard && messageForNoPlanCard.trim() !== "" && (
              <Grid item className="norstella-cf-noPlanCardMessage" part="cfMessage">
                {messageForNoPlanCard}
              </Grid>
            )
          }
          {showComponents && customFooter && customFooter.trim() !== "" && (
            <Grid item className="norstella-cf-GridItemFooter norstella-cf-topMargin">
              <Footer customFooter={customFooter} />
            </Grid>
          )}
          {debugMode && (
            <Grid item>
              <DebugMessage />
            </Grid>
          )}
        </Box>
      }
    </>
  );
};

export default PatientCoverageFinder;
